import { FC } from 'react'
import { PortableText, PortableTextComponents } from '@portabletext/react'
import { PortableTextBlock } from '@sanity/types'
import Image from 'next/image'
import { getSanityImageUrl } from '@/app/api/sanity'
import '@/app/styles/components/ArticleContent.css'

interface Props {
	content: PortableTextBlock[]
}

const ArticleContent: FC<Props> = ({ content }) => {
	const components: PortableTextComponents = {
		types: {
			image: ({ value }) => {
				const imageUrl = getSanityImageUrl(value, { width: 800, quality: 80 })
				if (!imageUrl) return null
				return (
					<div className="article-content__image-wrapper">
						<Image
							src={imageUrl}
							alt={value.alt || 'Article Image'}
							width={800}
							height={500}
							className="article-content__image"
						/>
						{value.caption && (
							<p className="article-content__image-caption">{value.caption}</p>
						)}
					</div>
				)
			},
		},
		block: {
			h2: ({ children }) => <h2 className="article-content__h2">{children}</h2>,
			h3: ({ children }) => <h3 className="article-content__h3">{children}</h3>,
			h4: ({ children }) => <h4 className="article-content__h4">{children}</h4>,
		blockquote: ({ children }) => (
			<blockquote className="article-content__blockquote">
				&ldquo;{children}&rdquo;
			</blockquote>
		),
			normal: ({ children }) => <p className="article-content__p">{children}</p>,
		},
		marks: {
			quoteAuthor: ({ children, value }) => (
				<span className="article-content__quote-author-container">
					<span className="article-content__quote-text">&ldquo;{children}&rdquo;</span>
					{value?.author && (
						<span className="article-content__quote-author-name">
							— {value.author}
						</span>
					)}
				</span>
			),
		},
		list: {
			bullet: ({ children }) => (
				<ul className="article-content__ul">{children}</ul>
			),
			number: ({ children }) => (
				<ol className="article-content__ol">{children}</ol>
			),
		},
		listItem: {
			bullet: ({ children }) => (
				<li className="article-content__li">{children}</li>
			),
			number: ({ children }) => (
				<li className="article-content__li">{children}</li>
			),
		},
	}

	return (
		<div className="article-content">
			<PortableText value={content} components={components} />
		</div>
	)
}

export default ArticleContent
